import {
  CreditCard,
  LayoutGrid,
  Megaphone,
  MapPin,
  MessageSquareText,
  Sun,
  Timer,
  X,
  Menu,
  UserCircle,
} from "lucide-react";
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import "../styles/scrollbar.css";
import { useAuth } from "../auth/AuthContext";
import { isSidebarItemAllowedByPackage } from "../lib/packageModules";
import EmployeeLoginDrawer from "./EmployeeLoginDrawer";
import { BetaBadge } from "./ui/BetaBadge";

interface SidebarItem {
  id: string;
  label: string;
  icon: string | React.ElementType;
  path: string;
  badge?: "beta";
}

const allMainItems: SidebarItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: "/bars/dashboard.svg",
    path: "/dashboard",
  },
  {
    id: "calendar",
    label: "Calendar",
    icon: "/bars/calendar.svg",
    path: "/calendar",
  },
  {
    id: "bookings",
    label: "Bookings",
    icon: "/bars/bookings.svg",
    path: "/bookings",
  },
  { id: "pos", label: "POS", icon: "/bars/pos.svg", path: "/pos" },
  {
    id: "website",
    label: "Website",
    icon: "/bars/website.svg",
    path: "/website",
  },
  {
    id: "marketing",
    label: "Marketing",
    icon: Megaphone,
    path: "/marketing",
  },
  {
    id: "payments",
    label: "Payments",
    icon: CreditCard,
    path: "/payments",
    badge: "beta",
  },
  { id: "manage", label: "Manage", icon: LayoutGrid, path: "/manage" },
  {
    id: "customers",
    label: "Customers",
    icon: "/bars/customers.svg",
    path: "/customers",
  },
  {
    id: "consultation",
    label: "Consultation",
    icon: MessageSquareText,
    path: "/consultation",
  },
  {
    id: "sunbeds",
    label: "Sunbeds",
    icon: Sun,
    path: "/sunbeds",
  },
  {
    id: "sunbed-session",
    label: "Sunbed Session",
    icon: Timer,
    path: "/sunbed-session",
  },
  {
    id: "reports",
    label: "Reports",
    icon: "/bars/reports.svg",
    path: "/reports",
  },
];

const superAdminMainItems: SidebarItem[] = [
  { id: "superadmin-dashboard", label: "Dashboard", icon: "/bars/dashboard.svg", path: "/superadmin/dashboard" },
  { id: "superadmin-companies", label: "Companies", icon: "/bars/customers.svg", path: "/superadmin/companies" },
  { id: "superadmin-packages", label: "Packages", icon: LayoutGrid, path: "/superadmin/packages" },
  { id: "superadmin-invoices", label: "Invoices", icon: "/bars/reports.svg", path: "/superadmin/invoices" },
  { id: "superadmin-offline-requests", label: "Offline Request", icon: "/bars/reports.svg", path: "/superadmin/offline-requests" },
  { id: "superadmin-locations", label: "Locations", icon: MapPin, path: "/superadmin/locations" },
  { id: "superadmin-categories", label: "Categories", icon: LayoutGrid, path: "/superadmin/categories" },
  { id: "superadmin-reports", label: "Reports", icon: "/bars/reports.svg", path: "/superadmin/reports" },
  { id: "superadmin-login-logs", label: "Login Logs", icon: "/bars/reports.svg", path: "/superadmin/login-logs" },
  { id: "superadmin-user-activity-logs", label: "User Activity Logs", icon: "/bars/reports.svg", path: "/superadmin/user-activity-logs" },
];

const employeeLoginItem = {
  id: "employee-login",
  label: "Employee login",
  icon: UserCircle,
  path: "#",
} as const;

const footerItem: SidebarItem = {
  id: "settings",
  label: "Settings",
  icon: "/bars/settings.svg",
  path: "/settings",
};

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  isDesktop?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose, isDesktop = false }) => {
  const { user } = useAuth();
  const [isTabletExpanded, setIsTabletExpanded] = useState(false);
  const [employeeLoginDrawerOpen, setEmployeeLoginDrawerOpen] = useState(false);

  // Check if user is an employee
  const isEmployee = React.useMemo(() => {
    const roles = user?.roles || [];
    return roles.some((role: any) => role?.name === "employee");
  }, [user]);

  const isSuperAdmin = React.useMemo(() => {
    const roles = user?.roles || [];
    return roles.some((role: any) => role?.name === "superadmin" || role?.name === "agent");
  }, [user]);

  const isAdmin = !isEmployee && !isSuperAdmin;

  const isSunbedEnabled = React.useMemo(() => {
    const setting = user?.company?.is_sunbed;
    if (setting === undefined || setting === null) return false;
    if (typeof setting === 'string') return setting === 'active';
    return Number(setting) === 1;
  }, [user]);

  const isClaimed = user?.company?.is_claim === "yes";
  const showAllModulesForTrial = Number(user?.company?.package_id) === 1;

  const filteredItems = (isSuperAdmin ? superAdminMainItems : allMainItems).filter((item: SidebarItem) => {
    if (isSuperAdmin) {
      return true;
    }

    if (!isSidebarItemAllowedByPackage(user, item.id)) {
      return false;
    }

    // Existing rule: hide POS + Calendar for unclaimed businesses.
    // Trial bypass: show all assigned modules for trial package to avoid onboarding friction.
    if (!showAllModulesForTrial && !isClaimed && (item.id === "pos" || item.id === "calendar")) {
      return false;
    }

    // For employees, only show specific menu items
    if (isEmployee) {
      const allowedItems = ["dashboard", "bookings", "consultation", "calendar", "pos"];
      // Include sunbeds and sunbed-session only if sunbed is enabled
      if (isSunbedEnabled && (item.id === "sunbeds" || item.id === "sunbed-session")) {
        return true;
      }
      return allowedItems.includes(item.id);
    }

    // For non-employees, apply normal filtering
    if (!showAllModulesForTrial && (item.id === "sunbeds" || item.id === "sunbed-session")) {
      return isSunbedEnabled;
    }
    return true;
  }).map(item => {
    // Dynamically set dashboard path based on role
    if (item.id === "dashboard") {
      return {
        ...item,
        path: isEmployee ? "/employee/dashboard" : isSuperAdmin ? "/superadmin/dashboard" : "/dashboard"
      };
    }
    return item;
  });

  const settingsItem = {
    ...footerItem,
    path: isSuperAdmin ? "/superadmin/settings" : "/settings",
  };

  const renderNavLink = (item: SidebarItem) => {
    const isString = typeof item.icon === "string";
    const IconComponent = !isString ? (item.icon as React.ElementType) : null;

    return (
      <NavLink
        key={item.id}
        to={item.path}
        onClick={onClose}
        className={({ isActive }) =>
          `flex items-center space-x-3 px-3 py-2 rounded-md cursor-pointer transition
           ${isDesktop
            ? "justify-start px-3 space-x-3"
            : isTabletExpanded
              ? "md:justify-start md:space-x-3 md:px-3"
              : "md:px-2 md:space-x-0 md:justify-center"
          }
          ${isActive
            ? "bg-purple-100 text-purple-700 font-semibold"
            : "text-black hover:bg-gray-100"
          }`
        }
      >
        {({ isActive }) => (
          <>
            <div
              className={`w-9 h-9 flex items-center justify-center rounded-md shrink-0
              ${isActive ? "bg-purple-600" : "bg-gray-100"}`}
            >
              {isString ? (
                <img
                  src={item.icon as string}
                  alt={item.label}
                  className={`w-5 h-5 ${isActive ? "filter brightness-0 invert" : ""
                    }`}
                />
              ) : (
                IconComponent && (
                  <IconComponent
                    size={20}
                    className={isActive ? "text-white" : "text-purple-600"}
                  />
                )
              )}
            </div>
            <span
              className={`text-sm flex items-center gap-1.5 min-w-0 ${isDesktop ? "flex" : isTabletExpanded ? "md:flex" : "md:hidden"}`}
            >
              <span className="truncate">{item.label}</span>
              {item.badge === "beta" && <BetaBadge className="shrink-0" />}
            </span>
          </>
        )}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[105] md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar Container */}
      <div
        className={`bg-white flex flex-col border-r border-gray-200 transition-all duration-300
          fixed inset-y-0 left-0 h-full w-64 shadow-2xl p-4 z-[110]
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 md:static md:h-screen md:flex md:shadow-none md:z-auto
          ${isDesktop ? "w-56 p-0" : (isTabletExpanded ? "md:w-56" : "md:w-[70px]")}
        `}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Close button for mobile */}
          <div className="flex justify-end mb-4 md:hidden">
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-md"
            >
              <X size={20} />
            </button>
          </div>

          {/* Tablet Toggle Button — shown between md and desktop breakpoint */}
          <div
            className={`hidden md:flex mb-4 ${isDesktop ? "!hidden" : ""} ${isTabletExpanded ? "justify-start px-0" : "justify-center"
              }`}
          >
            <button
              onClick={() => setIsTabletExpanded(!isTabletExpanded)}
              className={`flex items-center space-x-3 py-2 rounded-md cursor-pointer transition hover:bg-gray-100 ${isTabletExpanded ? "px-3 w-full" : "px-2 justify-center"
                }`}
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-md shrink-0 bg-gray-100">
                {isTabletExpanded ? (
                  <X size={20} className="text-purple-600" />
                ) : (
                  <Menu size={20} className="text-purple-600" />
                )}
              </div>
              {isTabletExpanded && (
                <span className="text-sm font-semibold">Menu</span>
              )}
            </button>
          </div>

          <div className={`flex-1 overflow-y-auto space-y-2 ${isDesktop ? "p-4 sidebar-scrollbar" : "hide-scrollbar"}`}>
            <nav className="space-y-2">{filteredItems.map(renderNavLink)}</nav>
          </div>

          <div className="border-t border-gray-100 space-y-2 pt-4 pb-4 pr-4 pl-3 [&_button>div:first-child]:-translate-x-0.5 [&_a>div:first-child]:-translate-x-0.5">
            {isAdmin && (
              <button
                type="button"
                onClick={() => {
                  setEmployeeLoginDrawerOpen(true);
                  onClose?.();
                }}
                className={`flex items-center rounded-md cursor-pointer transition w-full text-left text-black hover:bg-gray-100 py-2
                  ${isDesktop ? "justify-start px-3 space-x-3" : isTabletExpanded ? "justify-start px-3 space-x-3 md:justify-start md:px-3 md:space-x-3" : "justify-center px-2 md:justify-center md:px-2 md:space-x-0"}`}
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-md shrink-0 bg-gray-100">
                  <UserCircle size={20} className="text-purple-600" />
                </div>
                <span className={`text-sm whitespace-nowrap ${(isDesktop || isTabletExpanded) ? "block" : "md:hidden"}`}>
                  {employeeLoginItem.label}
                </span>
              </button>
            )}
            {(isSuperAdmin || isSidebarItemAllowedByPackage(user, footerItem.id)) && renderNavLink(settingsItem)}
          </div>
        </div>
      </div>

      <EmployeeLoginDrawer
        isOpen={employeeLoginDrawerOpen}
        onClose={() => setEmployeeLoginDrawerOpen(false)}
      />
    </>
  );
};

export default Sidebar;
